import useCreation from "ahooks/es/useCreation";
import { addDays, isValid, isWeekend, parseISO } from "date-fns";
import { useSearchParams } from "react-router-dom";
import { useBackofficePermissions } from "../../../../modules/permissions/hooks/useBackofficePermissions";
import { AnalyticsDateRange } from "../domain";

export interface UseReservationDateResult {
  dateRange: AnalyticsDateRange;
}

const preselectedDate = getFirstWorkingDay(new Date());

function useParseDate(dateString: string | null): Date {
  return useParseDateOrDefault(dateString, preselectedDate);
}

function useParseDateOrDefault(
  dateString: string | null,
  defaultDate: Date
): Date {
  return useCreation(() => {
    if (!dateString) {
      return defaultDate;
    }

    const date = parseISO(dateString);
    if (isValid(date)) {
      return date;
    }
    return defaultDate;
  }, [dateString, defaultDate]);
}

function getFirstWorkingDay(after: Date): Date {
  while (isWeekend(after)) {
    after = addDays(after, 1);
  }
  return after;
}

export function useReservationDate(): UseReservationDateResult {
  const [searchParams] = useSearchParams();
  const startDate = useParseDate(searchParams.get("date"));
  const endDate = useParseDateOrDefault(
    searchParams.get("date-end"),
    startDate
  );
  const { isAdmin } = useBackofficePermissions();
  const dateRange = useCreation(() => {
    return AnalyticsDateRange.of(startDate, endDate, isAdmin);
  }, [startDate, endDate]);
  return { dateRange };
}
